import {
  CameraTransitionPreset,
  Coroutine,
  DracoDecoderSource,
  IAdvancedViewer,
  IDirectionalLight,
  IExperimentalViewer,
  IVector3,
  IViewer,
  LightType,
  create_viewer
} from '@inter-ikea-betrakta/viewer';
import { smooth_step } from '@inter-ikea-betrakta/viewer/dist/index';
import { InputGatherer } from '@inter-ikea-betrakta/viewer/dist/modules/InputGatherer';
import { OrbitPlannerCamera } from '@inter-ikea-betrakta/viewer/dist/modules/cameras/OrbitPlannerCamera';
import { ComponentIds, Core, Entity, Viewer } from '@inter-ikea-core/core';
import { IPEXViewerCoreAdapter } from '@inter-ikea-core/ipex-betrakta-core-adapter';
import { captureException } from '@sentry/react';
import { sceneAction } from 'redux-store/scene/scene.store';
import store from 'redux-store/store';
import { defaultViewerCalculations } from 'utils/constants';
import { OutlineColor } from 'utils/constants/colors';
import { RENDER_CANVAS } from 'utils/constants/elements';
import delay from 'utils/delay';
import getAssetsPath from 'utils/getAssetsPath';
import { isE2eOrUnitTest, isE2eTest, isKiosk, isProduction } from 'utils/platform';
import cameraSettings from './cameraSettings';
import ProductPlacementUtil from './productPlacementUtil';
import screenshotSettings from './screenshotSettings';
import { RoomRenderingSystem, ViewerRoomComponent } from './systems/RoomRenderingSystem';

let orbitCamera: OrbitPlannerCamera;
let orbitCameraScreenshot: OrbitPlannerCamera;
let ipexViewer: IViewer;
let directionalLight: IDirectionalLight;
export let roomRenderingSystem: RoomRenderingSystem | undefined;
let productPlacementUtil: ProductPlacementUtil | undefined;
let core: Core | undefined;

interface ICameraAnimationParams {
  duration: number;
  position: IVector3;
  camera: OrbitPlannerCamera;
}

export const getBetraktaCrashReport = () => {
  const viewer = ipexViewer as IViewer & IExperimentalViewer & IAdvancedViewer;

  if (!viewer) {
    return;
  }

  // Create report only for kiosks for now [SKYT-1547]
  const canCreateReport = isKiosk() && !isE2eTest();
  if (canCreateReport) {
    const memory_tracker = viewer.get_webgl_memory_tracker();
    const memory_tracker_report = !memory_tracker
      ? 'null'
      : memory_tracker.get_memory_usage_report();

    //@ts-expect-error
    const effects = viewer._engine._compiledEffects;

    let babylon_shader_cache_count = 0;
    let babylon_shader_cache_waste_bytes = 0;

    try {
      const keys = Object.keys(effects);
      babylon_shader_cache_count = 0;
      keys.forEach(key => {
        const effect = effects[key];
        babylon_shader_cache_count++;
        babylon_shader_cache_waste_bytes += key.length;
        babylon_shader_cache_waste_bytes += effect._fragmentSourceCode.length;
        babylon_shader_cache_waste_bytes += effect._vertexSourceCode.length;
        babylon_shader_cache_waste_bytes += effect.fragmentSourceCodeBeforeMigration.length;
        babylon_shader_cache_waste_bytes += effect.vertexSourceCodeBeforeMigration.length;

        babylon_shader_cache_waste_bytes += effect._fragmentSourceCodeOverride.length;
        babylon_shader_cache_waste_bytes += effect._vertexSourceCodeOverride.length;
      });
    } catch {
    } finally {
      const report = {
        webgl_usage: memory_tracker_report,
        babylon_shader_cache_count: babylon_shader_cache_count,
        babylon_shader_cache_waste_bytes: babylon_shader_cache_waste_bytes
      };

      return report;
    }
  }
  return null;
};

// coroutine function
export function* rotateOnAngleCoroutine(
  cameraParams: ICameraAnimationParams,
  on_done: () => void
): Coroutine {
  const { duration, position, camera } = cameraParams;
  const startPos = IVector3.copy(position);
  const endPos = cameraCoordinates().position;
  let elapsedTime = 0;
  const cameraZPos = IVector3.copy(camera.position).z;

  while (elapsedTime < duration) {
    const delta = yield;
    elapsedTime += delta.milliseconds;
    const clampedTime = Math.min(1, elapsedTime / duration);
    const easedTime = smooth_step(clampedTime);

    const newX = startPos.x + (endPos.x - startPos.x) * easedTime;
    const newY = startPos.y + (endPos.y - startPos.y) * easedTime;
    const newZ = cameraZPos + (startPos.z - cameraZPos) * easedTime;

    camera.look_at({ x: newX, y: newY, z: newZ }, cameraCoordinates().target);
  }

  on_done();
  return undefined;
}

export const getViewer = () => {
  if (!ipexViewer) {
    const canvas = document.getElementById('renderCanvas') as HTMLCanvasElement | null;

    if (!canvas) {
      captureException(new Error('No canvas was ready to assign viewer with.'));
      return undefined;
    }

    let dracoSource: DracoDecoderSource = DracoDecoderSource.UNIVERSAL;

    const isIkeaCom =
      window.location.host.startsWith('www.ikea.com') ||
      window.location.host.startsWith('ikea.com');

    if (isIkeaCom && !isKiosk()) {
      dracoSource = DracoDecoderSource.IKEA_COM;
    }

    ipexViewer = create_viewer(canvas, dracoSource, {
      headless: isE2eOrUnitTest() && !isProduction(),
      enable_webgl_memory_tracker: isKiosk() ? true : undefined
    });
  }

  return ipexViewer;
};

export const getScreenshotCamera = () => {
  if (!orbitCameraScreenshot) {
    orbitCameraScreenshot = new OrbitPlannerCamera(
      cameraCoordinates().position,
      cameraCoordinates().target,
      ipexViewer
    );
    orbitCameraScreenshot.field_of_view_degrees = defaultViewerCalculations.fieldOfView;
    orbitCameraScreenshot.update_aspect_ratio(screenshotSettings.aspectRatio);
  }
  return orbitCameraScreenshot;
};

export const getCamera = () => {
  if (!orbitCamera) {
    orbitCamera = new OrbitPlannerCamera(
      cameraCoordinates().position,
      cameraCoordinates().target,
      ipexViewer
    );
    orbitCamera.field_of_view_degrees = defaultViewerCalculations.fieldOfView;
    ipexViewer.replace_camera(orbitCamera, CameraTransitionPreset.NONE);
  }
  return orbitCamera;
};

export const restrictCameraMovement = () => {
  const activeCamera = getCamera();
  const camTarget = activeCamera.target;
  // Will lock camera to one position, so we do not allow panning
  activeCamera.set_pan_limits({ min: camTarget, max: camTarget });
  // Will lock camera zoom to not be too close nor too far away
  activeCamera.set_zoom_limits({
    near: defaultViewerCalculations.limitsMinMax.min,
    far: defaultViewerCalculations.limitsMinMax.max
  });
};

export const onResize = () => {
  const canvas = document.getElementById('renderCanvas') as HTMLCanvasElement | null;
  if (!canvas) {
    return;
  }

  const { width: canvasWidth, height: canvasHeight } = canvas.getBoundingClientRect();
  let renderWidth = canvasWidth;
  let renderHeight = canvasHeight;

  if (renderWidth < 1 || renderHeight < 1) {
    const isMobile = store.getState().userAgent.isMobile;
    if (isMobile) {
      renderWidth = screenshotSettings.params.size.mobile.x;
      renderHeight = screenshotSettings.params.size.mobile.y;
    } else {
      renderWidth = screenshotSettings.params.size.desktop.x;
      renderHeight = screenshotSettings.params.size.desktop.y;
    }
  }

  if (renderWidth < 1 || renderHeight < 1) {
    renderWidth = 1920;
    renderHeight = 1080;
  }

  ipexViewer.resize(renderWidth, renderHeight);
};

export const addAmbientOcclusion = (enabled: boolean) => {
  ipexViewer.enable_temporal_effects(enabled);
};

export const createViewerDefaultSetup = (
  importedCore: Core,
  importedProductPlacementUtil: ProductPlacementUtil
) => {
  productPlacementUtil = importedProductPlacementUtil;
  core = importedCore;
  const canvas = document.getElementById(RENDER_CANVAS) as unknown as HTMLCanvasElement; // TS requires to first convert to unknown

  ipexViewer = getViewer() as IViewer;
  const inputGatherer = new InputGatherer(canvas, ipexViewer);
  ipexViewer.get_viewer_dispose_observable().add_once(() => {
    return inputGatherer.dispose();
  });
  getCamera();
  restrictCameraMovement();
  setupEnvironmentMap();
  setupLight();
  addAmbientOcclusion(true);
  const coreViewer: Viewer = setupCoreViewerBridge(importedCore);
  overrideInitialTabIndex();

  ipexViewer.set_global_outline_color(OutlineColor.hover);
  ipexViewer.set_global_outline_width(0.3);
  return { ipexViewer, coreViewer };
};

export const awaitVisibleModelsOnScene = async () => {
  if (!ipexViewer) {
    return false;
  }

  return ipexViewer.on_current_scene_ready().then(async () => {
    /**
     * Making sure all loading boxes are gone before confirming models are loaded
     * This is needed for the screenshotUtil to not risk capturing boxes when going straight to summary from url
     *
     * loadingBoxAnimationDuration is taken from here:
     * https://github.com/inter-ikea/CBF-RE-IPEX-VIEWER/blob/main/package/viewer/src/modules/ProxyLoader.ts#L61
     */
    const loadingBoxAnimationDuration = 300;
    const objectIds = ipexViewer.get_all_objects();
    const promises: Array<Promise<void>> = [];
    objectIds.forEach(id => {
      promises.push(ipexViewer.object_ready(id));
    });
    await Promise.all(promises);
    await delay(loadingBoxAnimationDuration);
    onResize();
    store.dispatch(sceneAction.setHasAllSelectedModelsLoaded(true));
    return true;
  });
};

const setupCoreViewerBridge = (core: Core) => {
  const coreViewer: Viewer = new IPEXViewerCoreAdapter(
    ipexViewer,
    core.services.entityManager,
    core.services.sceneManager
  );
  core.setViewer(coreViewer);

  roomRenderingSystem = new RoomRenderingSystem(core, ipexViewer);
  core.services.entityManager.registerSystem(roomRenderingSystem);

  return coreViewer;
};

const overrideInitialTabIndex = () => {
  // It seems Babylon requires tabIndex on the canvas to be 1 to allow keyboard inputs so maybe this is a bad idea,
  // but it is needed for a11y reasons so the tab order makes sense.
  // https://forum.babylonjs.com/t/canvas-tabindex-1/8052
  ipexViewer.set_canvas_tab_index(-1);
};

const centeredRoomDepthTarget = () => {
  const fromMMtoM = (value: number) => {
    return value / 1000;
  };

  const roomDepth = productPlacementUtil?.userSelections.selectedRoomDepth;
  return roomDepth ? fromMMtoM(roomDepth) / 2 : defaultViewerCalculations.targetXYZ.z;
};

export const cameraCoordinates = () => {
  return {
    initialPosition: new IVector3(
      defaultViewerCalculations.preIntroAnimationX,
      defaultViewerCalculations.positionXYZ.y,
      defaultViewerCalculations.positionXYZ.z
    ),
    position: new IVector3(
      defaultViewerCalculations.positionXYZ.x,
      defaultViewerCalculations.positionXYZ.y,
      defaultViewerCalculations.positionXYZ.z
    ),
    target: new IVector3(
      defaultViewerCalculations.targetXYZ.x,
      defaultViewerCalculations.targetXYZ.y,
      centeredRoomDepthTarget()
    )
  };
};

const cameraDirection = () => {
  const position = new IVector3(
    defaultViewerCalculations.positionXYZ.x,
    defaultViewerCalculations.positionXYZ.y,
    defaultViewerCalculations.positionXYZ.z
  );
  const target = new IVector3(
    defaultViewerCalculations.targetXYZ.x,
    defaultViewerCalculations.targetXYZ.y,
    centeredRoomDepthTarget()
  );

  const subtractedPosTar = IVector3.subtract(target, position);
  const direction = IVector3.normalize(subtractedPosTar);
  return direction;
};

const frameObjects = (camera: OrbitPlannerCamera, direction: IVector3) => {
  let cameraTargets: number[] = [];

  if (core) {
    const { entities } = core.services.entityManager;

    const testRoomEntity = entities.find(
      (entity: Entity) =>
        entity.hasComponent(ComponentIds.ROOM_GEOMETRY_COMPONENT_ID) &&
        entity.hasComponent(ViewerRoomComponent.ID)
    );

    if (testRoomEntity) {
      const roomComponent = testRoomEntity.getComponent<ViewerRoomComponent>(
        ViewerRoomComponent.ID
      );
      const viewerObjects = roomComponent?.viewerRoom.get_room_objects();

      if (viewerObjects?.length) {
        cameraTargets = viewerObjects;
      }
    }
  }

  if (!cameraTargets.length) {
    return;
  }

  return camera.frame_objects(
    cameraTargets,
    cameraSettings.precise,
    cameraSettings.margin,
    direction,
    cameraSettings.aspectRatio
  );
};

export const setCameraRelativeToRoom = async (
  direction?: IVector3,
  camera?: OrbitPlannerCamera,
  noAnimate?: boolean,
  dontAwaitModels?: boolean
) => {
  if (!ipexViewer) {
    return;
  }

  onResize();

  if (!dontAwaitModels) {
    const {
      scene: { hasAllSelectedModelsLoaded }
    } = store.getState();

    const allModelsVisible = await awaitVisibleModelsOnScene();

    if (!hasAllSelectedModelsLoaded && allModelsVisible) {
      store.dispatch(sceneAction.setHasAllSelectedModelsLoaded(true));
    }
  }

  direction = direction || cameraDirection();

  const activeCamera = camera || getCamera();
  if (noAnimate) {
    await frameObjects(activeCamera, direction);
  } else {
    const tempCamera = activeCamera.clone();

    await frameObjects(tempCamera, direction);

    await new Promise<void>(resolve => {
      ipexViewer.start_coroutine(
        rotateOnAngleCoroutine(
          { duration: 750, position: tempCamera.position, camera: getCamera() },
          () => {
            return resolve();
          }
        )
      );
    });

    tempCamera.dispose();
  }

  restrictCameraMovement();
};

const setupLight = () => {
  directionalLight = ipexViewer.create_light(LightType.DIRECTIONAL_LIGHT);
  setLightPosition();
  directionalLight.target = new IVector3(0, 0, 0);
  directionalLight.intensity = 3;
  directionalLight.shadow.enable_shadows = true;
  directionalLight.shadow.shadow_near = 1;
  directionalLight.shadow.shadow_far = 25.0;
  directionalLight.shadow.shadow_resolution = 2048;
};

const setLightPosition = (direction?: IVector3) => {
  const xCoord = 5.8;
  const YCoord = 10;
  const defaultZCoord = 3;
  let zCoord;

  if (direction) {
    zCoord = direction.z > 0 ? -defaultZCoord : defaultZCoord;
  } else {
    zCoord = getCamera().position.z < 0 ? -defaultZCoord : defaultZCoord;
  }

  directionalLight.position = new IVector3(xCoord, YCoord, zCoord);
};

export const setShadows = (status: boolean) => {
  directionalLight.shadow.enable_shadows = status;
};

const setupEnvironmentMap = () => {
  ipexViewer.set_environment_map(getAssetsPath('./210609_pax_env_lowres_2.env'));
  ipexViewer.set_environment_map_rotation_radians(29.9);
  ipexViewer.set_environment_map_local_mode({
    size: { x: 4.5, y: 5, z: 5.5 },
    position: { x: 0, y: 1.5, z: 2 }
  });
  ipexViewer.set_environment_map_intensity(0.95);
};
